import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import './Home.css';

const CountUp = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    const element = document.getElementById(`count-${end}`);
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [end, isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    let animationFrame;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [end, duration, isVisible]);

  return <span id={`count-${end}`}>{count}{suffix}</span>;
};

const Home = () => {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState(new Set());

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('.section-reveal');
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          section.classList.add('visible');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="home-page">
      <PublicNavbar />
      
      {/* Hero Section */}
      <section className="hero-section text-white py-5" style={{ minHeight: '60vh', background: 'linear-gradient(135deg, #00428a 0%, #55c900 100%)' }}>
        <Container>
          <Row className="justify-content-center text-center py-5">
            <Col xl={7} lg={9}>
              <h1 className="display-4 fw-bold mb-4 animate-fade-in-up">Fueling Your Future with Technology & Training</h1>
              <p className="lead mb-4 animate-fade-in delay-200">
                BHIT Infotech is a dedicated provider of IT training and technology services, founded with a vision to bridge the gap between education and employability in the digital world.
              </p>
              <p className="mb-4 animate-fade-in delay-300">
                Our Mission is to deliver high-quality, affordable, and industry-relevant IT training and services that equip learners and clients with the skills and tools needed to succeed in today's digital economy.
              </p>
              <Button style={{ backgroundColor: '#55c900', borderColor: '#55c900' }} size="lg" onClick={() => navigate('/register')} className="animate-bounce btn-scale">
                Get Started
              </Button>
            </Col>
          </Row>

          <Row className="g-4 mt-4">
            <Col md={6} lg={3} className="animate-fade-in-up delay-400">
              <Card className="text-center h-100 border-0 shadow-sm card-hover-lift">
                <Card.Body>
                  <div className="mb-3 animate-float">
                    <i className="bi bi-laptop" style={{ fontSize: '3rem', color: '#00428a' }}></i>
                  </div>
                  <Card.Title>IT Training Programs</Card.Title>
                  <Card.Text>
                    Courses in Python, Java, Web Development, Data Science, Cloud Computing, and more — designed for students, graduates, and working professionals.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={3} className="animate-fade-in-up delay-500">
              <Card className="text-center h-100 border-0 shadow-sm card-hover-lift">
                <Card.Body>
                  <div className="mb-3 animate-float">
                    <i className="bi bi-code-slash" style={{ fontSize: '3rem', color: '#00428a' }}></i>
                  </div>
                  <Card.Title>Software Development Services</Card.Title>
                  <Card.Text>
                    Custom applications, websites, and automation solutions for startups and SMEs.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={3} className="animate-fade-in-up delay-600">
              <Card className="text-center h-100 border-0 shadow-sm card-hover-lift">
                <Card.Body>
                  <div className="mb-3 animate-float">
                    <i className="bi bi-diagram-3" style={{ fontSize: '3rem', color: '#00428a' }}></i>
                  </div>
                  <Card.Title>Tech Consulting</Card.Title>
                  <Card.Text>
                    Guidance for IT project planning, digital transformation, and process optimization.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={3} className="animate-fade-in-up delay-700">
              <Card className="text-center h-100 border-0 shadow-sm card-hover-lift">
                <Card.Body>
                  <div className="mb-3 animate-float">
                    <i className="bi bi-person-arms-up" style={{ fontSize: '3rem', color: '#00428a' }}></i>
                  </div>
                  <Card.Title>Career Support</Card.Title>
                  <Card.Text>
                    Interview preparation, resume building, and placement assistance for job seekers in tech.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* About Section */}
      <section id="about" className="about-section py-5 bg-light section-reveal">
        <Container>
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold gradient-text">About Us</h2>
            <p className="lead">
              <strong>BHIT Infotech</strong> is a leading provider of IT training and technology services, committed to delivering practical, industry-ready skills for students, professionals, and businesses.
            </p>
          </div>

          <Row className="gy-4 align-items-center">
            <Col lg={6} className="animate-slide-left">
              <p>
                We specialize in high-demand areas such as Python, Java, Web Development, Data Science, and Cloud Technologies. Our approach is hands-on and tailored, helping learners not only gain knowledge but apply it confidently in real-world projects.
              </p>
              <ul className="list-unstyled">
                <li className="mb-2">✅ Industry-relevant training programs</li>
                <li className="mb-2">✅ Software development and IT consulting services</li>
                <li className="mb-2">✅ Placement support and career guidance</li>
              </ul>
            </Col>

            <Col lg={6} className="animate-slide-right">
              <p>
                With a focus on innovation, skill development, and digital empowerment, BHIT Infotech is your trusted partner in today's fast-moving tech landscape.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="stats-section py-5 section-reveal">
        <Container>
          <Row className="g-4 text-center">
            <Col lg={3} md={6} className="animate-fade-in-up delay-100">
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <h2 className="display-4 fw-bold" style={{ color: '#55c900' }}>
                    <CountUp end={30} suffix="+" />
                  </h2>
                  <p className="text-muted">Clients</p>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6} className="animate-fade-in-up delay-200">
              <Card className="border-0 shadow-sm h-100 card-hover-lift">
                <Card.Body>
                  <h2 className="display-4 fw-bold animate-pulse" style={{ color: '#55c900' }}>
                    <CountUp end={50} suffix="+" />
                  </h2>
                  <p className="text-muted">Projects</p>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6} className="animate-fade-in-up delay-300">
              <Card className="border-0 shadow-sm h-100 card-hover-lift">
                <Card.Body>
                  <h2 className="display-4 fw-bold animate-pulse" style={{ color: '#55c900' }}>
                    <CountUp end={100} suffix="+" />
                  </h2>
                  <p className="text-muted">Hours Of Support</p>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6} className="animate-fade-in-up delay-400">
              <Card className="border-0 shadow-sm h-100 card-hover-lift">
                <Card.Body>
                  <h2 className="display-4 fw-bold" style={{ color: '#55c900' }}>
                    <CountUp end={12} />
                  </h2>
                  <p className="text-muted">Workers</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section py-5 bg-light section-reveal">
        <Container>
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold gradient-text">Services</h2>
            <p className="lead">
              We offer a range of IT training and technology services to help learners and businesses grow in the digital era.
            </p>
          </div>

          <Row className="g-4">
            {[
              { icon: 'bi-window-stack', title: 'Full Stack Development', desc: 'Hands-on training in front-end and back-end technologies including HTML, CSS, JavaScript, Node.js, and databases.' },
              { icon: 'bi-cpu', title: 'Python & Automation', desc: 'Master Python programming and build automation tools with real-world use cases in data, testing, and scripting.' },
              { icon: 'bi-cloud-arrow-up', title: 'Cloud & DevOps', desc: 'Get skilled in AWS, Azure, Docker, and CI/CD pipelines. Perfect for infrastructure and deployment roles.' },
              { icon: 'bi-bar-chart-line', title: 'Data Science & Analytics', desc: 'Learn Python for data, machine learning, and tools like Pandas, NumPy, and Power BI for real insights.' },
              { icon: 'bi-mortarboard', title: 'Placement Training', desc: 'Interview preparation, mock tests, resume building, and soft skills to get job-ready in today\'s IT market.' },
              { icon: 'bi-code-slash', title: 'Custom Software Solutions', desc: 'We design and develop custom web and mobile applications tailored to your business needs.' }
            ].map((service, idx) => (
              <Col lg={4} md={6} key={idx}>
                <Card className="h-100 border-0 shadow-sm card-hover-lift">
                  <Card.Body className="text-center">
                    <div className="mb-3 animate-float">
                      <i className={`bi ${service.icon}`} style={{ fontSize: '3rem', color: '#00428a' }}></i>
                    </div>
                    <Card.Title className="fw-bold">{service.title}</Card.Title>
                    <Card.Text className="text-muted">{service.desc}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="cta-section text-white py-5 section-reveal" style={{ background: 'linear-gradient(135deg, #00428a 0%, #55c900 100%)' }}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col xl={10}>
              <h2 className="display-5 fw-bold mb-4 animate-fade-in-up">Ready to Elevate Your Tech Career or Business?</h2>
              <p className="lead mb-4 animate-fade-in delay-200">
                Join BHIT Infotech today and gain the skills, tools, and support you need to thrive in the digital era. Whether you're a student, professional, or enterprise—our training and tech solutions are designed for impact.
              </p>
              <Button style={{ backgroundColor: '#55c900', borderColor: '#55c900' }} size="lg" onClick={() => navigate('/register')} className="animate-bounce btn-scale delay-300">
                Get Started Now
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section py-5 section-reveal">
        <Container>
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold gradient-text">Contact</h2>
            <p className="lead">We'd love to hear from you! Reach out with any questions or feedback.</p>
          </div>

          <Row className="gy-4">
            <Col lg={4} className="animate-fade-in-up delay-100">
              <Card className="border-0 shadow-sm h-100 card-hover-lift">
                <Card.Body className="text-center">
                  <i className="bi bi-geo-alt animate-float" style={{ fontSize: '2rem', color: '#00428a' }}></i>
                  <h5 className="mt-3">Address</h5>
                  <p className="text-muted">Srikakulam, Andhra Pradesh, India - 532284</p>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4} className="animate-fade-in-up delay-200">
              <Card className="border-0 shadow-sm h-100 card-hover-lift">
                <Card.Body className="text-center">
                  <i className="bi bi-telephone animate-float" style={{ fontSize: '2rem', color: '#00428a' }}></i>
                  <h5 className="mt-3">Call Us</h5>
                  <p className="text-muted">+91 6303 179 166</p>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4} className="animate-fade-in-up delay-300">
              <Card className="border-0 shadow-sm h-100 card-hover-lift">
                <Card.Body className="text-center">
                  <i className="bi bi-envelope animate-float" style={{ fontSize: '2rem', color: '#00428a' }}></i>
                  <h5 className="mt-3">Email Us</h5>
                  <p className="text-muted">noreply@bhitinfotech.com</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <PublicFooter />
    </div>
  );
};

export default Home;
